// frontend/src/components/StageView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import ContextMenu from './ContextMenu';
import SectionList from './SectionList';
import MusicSpectrogram from './MusicSpectrogram';

import DancerService from '../services/DancerService';
import SkillService from '../services/SkillService';
import PositionService from '../services/PositionService';

import defaultCursor from '../images/defaultCursor.svg';
import trashIcon from '../images/trashIcon.svg';
import { ReactComponent as DownloadIcon } from '../images/downloadIcon.svg';
import { ReactComponent as SettingsIcon } from '../images/settingsIcon.svg';

import { ReactComponent as ClosedLock } from '../images/closedLock.svg';
import { ReactComponent as OpenedLock } from '../images/openedLock.svg';



//PDF
import { jsPDF } from "jspdf";
import "jspdf-autotable";


// Feuille de style
import '../styles/StageView.css';


const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Petit hook pour récupérer la taille de la fenêtre
 */
function useWindowSize() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function onResize() {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return size;
}

/**
 * On garde un ratio de 1045 x 665 uniquement pour le canvas.
 * Les barres du haut et du bas (40px chacune) sont fixes.
 */
const TOP_BAR_HEIGHT = 45;
const CONTROL_BAR_HEIGHT = 22;
const BOTTOM_BAR_HEIGHT = 100 + CONTROL_BAR_HEIGHT;
// Hauteur de la timeline
const timelineHeight = 20;
const amplitudeSliderWidth = 25;


const CANVAS_BASE_WIDTH = 1045;
const CANVAS_BASE_HEIGHT = 665;
const CANVAS_RATIO = CANVAS_BASE_WIDTH / CANVAS_BASE_HEIGHT;

/**
 * StageView
 */
export default function StageView({ user }) {
  const { sessionId } = useParams();

  // -----------------------------
  // States
  // -----------------------------
  const [currentSession, setCurrentSession] = useState(null);
  const [dancers, setDancers] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [skills, setSkills] = useState([]);

  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [selectedDancerId, setSelectedDancerId] = useState(null);
  const [selectedSkillId, setSelectedSkillId] = useState(null);

  const [newPeriodName, setNewPeriodName] = useState('');
  const [newDancerName, setNewDancerName] = useState('');
  const [newSkillName, setNewSkillName] = useState('');

  // Playing
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const [volume, setVolume] = useState(1); // 1 correspond à 100%

  // Déclarer une référence pour mémoriser le dernier état "inAPeriod"
  const prevInPeriod = useRef(null);

  // Controlbar states
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isThreeDMode, setIsThreeDMode] = useState(false);

  // Optimisation 
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [forcedPosition, setForcedPosition] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Musique  
  const [musicIsDragging, setMusicIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Skill color
  const skillColors = [
    "#36949d", "#ff924c", "#1982c4", "#ffca3a",
    "#4267ac", "#c5ca30", "#565aa0", "#8ac926", "#6a4c93",
    "#e97c61", "#e9c46a", "#e76f51", "#8ab17d", "#ee8959",
    "#2a9d8f", "#f4a261", "#287271", "#efb366", "#264653", "#ff595e"
  ];
  const [colorIndex, setColorIndex] = useState(0);
  const [newSkillColor, setNewSkillColor] = useState(skillColors[colorIndex]); // Initialisation

  //
  const canvasRef = useRef(null);

  const [contextMenuPosition, setContextMenuPosition] = useState(null);

  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPosId, setDraggedPosId] = useState(null);

  const [pendingClickInfo, setPendingClickInfo] = useState(null);
  const THRESHOLD = 5;

  // Vue "scene" ou "trajectory"
  const [viewMode, setViewMode] = useState('scene');

  const navigate = useNavigate();

  // -----------------------------
  // Dimensions dynamiques
  // -----------------------------
  const windowSize = useWindowSize();
  const SIDEBAR_WIDTH = 280;  // barre latérale fixe
  const MIN_MARGIN = 10;      // marges sur les bords

  // 1) Espace horizontal disponible (fenêtre - sidebar - marges)
  const availableWidth = windowSize.width - SIDEBAR_WIDTH - 2 * MIN_MARGIN;

  const [cursorMode, setCursorMode] = useState('default'); 

  // Etats pour le menu contextuel (clic droit) sur le danseur côté volet gauche
  const [dancerContextMenu, setDancerContextMenu] = useState({
     x: 0,
     y: 0,
     visible: false,
     dancerId: null,
     options: [],
   });

  // 2) Espace vertical disponible (fenêtre - marges - barres top/bottom fixes)
  const availableHeight =
    windowSize.height - 2 * MIN_MARGIN - TOP_BAR_HEIGHT - BOTTOM_BAR_HEIGHT;

  // On calcule la taille du canvas selon le ratio
  let canvasWidth = availableWidth;
  let canvasHeight = canvasWidth / CANVAS_RATIO;

  // Si la hauteur dépasse la place dispo, on borne
  if (canvasHeight > availableHeight) {
    canvasHeight = availableHeight;
    canvasWidth = canvasHeight * CANVAS_RATIO;
  }

  // Dimensions finales du container global (canvas + 2 barres)
  const containerWidth = canvasWidth;
  const containerHeight = TOP_BAR_HEIGHT + canvasHeight + BOTTOM_BAR_HEIGHT;

  // Le canvas côté HTML (pour la partie "pixel" interne)
  const canvasPixelWidth = Math.round(canvasWidth);
  const canvasPixelHeight = Math.round(canvasHeight);


  // -----------------------------
  // Raccourcis claviers
  // -----------------------------

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Vérifier si l'utilisateur est en train de taper dans un champ texte
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable)) {
        return; // Ne pas intercepter les raccourcis clavier si on est dans une zone de texte
      }

      if (e.key === 'Tab') {
        e.preventDefault(); // Empêche le comportement par défaut

        if (e.shiftKey) {
          // Shift+Tab → Revient au danseur précédent
          if (dancers.length > 0) {
            setSelectedDancerId((prevId) => {
              const currentIndex = dancers.findIndex(d => d.id === prevId);
              const newIndex = (currentIndex - 1 + dancers.length) % dancers.length;
              const newDancer = dancers[newIndex];

              // Mettre à jour la position
              const period = periods[currentPeriodIndex];
              const dancerPosition = period?.positions.find(pos => pos.dancer_id === newDancer.id);
              setSelectedPositionId(dancerPosition ? dancerPosition.id : null);

              // Forcer le focus sur le nouveau danseur
              setTimeout(() => {
                const dancerElement = document.getElementById(`dancer-${newDancer.id}`);
                if (dancerElement) dancerElement.focus();
              }, 10);

              return newDancer.id;
            });
          }
        } else {
          // Tab → Passe au danseur suivant
          if (dancers.length > 0) {
            setSelectedDancerId((prevId) => {
              const currentIndex = dancers.findIndex(d => d.id === prevId);
              const newIndex = (currentIndex + 1) % dancers.length;
              const newDancer = dancers[newIndex];

              // Mettre à jour la position
              const period = periods[currentPeriodIndex];
              const dancerPosition = period?.positions.find(pos => pos.dancer_id === newDancer.id);
              setSelectedPositionId(dancerPosition ? dancerPosition.id : null);

              // Forcer le focus sur le nouveau danseur
              setTimeout(() => {
                const dancerElement = document.getElementById(`dancer-${newDancer.id}`);
                if (dancerElement) dancerElement.focus();
              }, 10);

              return newDancer.id;
            });
          }
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault(); // Empêche le comportement par défaut (ex: scrolling)
        // Flèche droite → Passe à la période suivante
        if (periods.length > 0) {
          setCurrentPeriodIndex((prevIndex) => {
            const newIndex = (prevIndex + 1) % periods.length;
      
            // Forcer le focus sur la nouvelle période
            setTimeout(() => {
              const periodElement = document.getElementById(`period-${periods[newIndex]?.id}`);
              if (periodElement) periodElement.focus();
            }, 10);
      
            // Mettre à jour la barre de progression audio
            const newPeriod = periods[newIndex];
            if (audioRef.current && newPeriod) {
              audioRef.current.currentTime = newPeriod.start_time;
              setCurrentTime(newPeriod.start_time);
            }
            return newIndex;
          });
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault(); // Empêche le comportement par défaut (ex: scrolling)
        // Flèche gauche → Revient à la période précédente
        if (periods.length > 0) {
          setCurrentPeriodIndex((prevIndex) => {
            const newIndex = (prevIndex - 1 + periods.length) % periods.length;
      
            // Forcer le focus sur la nouvelle période
            setTimeout(() => {
              const periodElement = document.getElementById(`period-${periods[newIndex]?.id}`);
              if (periodElement) periodElement.focus();
            }, 10);
      
            // Mettre à jour la barre de progression audio
            const newPeriod = periods[newIndex];
            if (audioRef.current && newPeriod) {
              audioRef.current.currentTime = newPeriod.start_time;
              setCurrentTime(newPeriod.start_time);
            }
            return newIndex;
          });
        }

      //Barre espace pour pause/play
      } else if (e.key === ' ') {
        e.preventDefault(); // Empêche le défilement par défaut de la barre espace
        setIsPlaying(prev => !prev); // Bascule entre lecture et pause
      }

    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dancers, selectedDancerId, periods, currentPeriodIndex]);



  // -----------------------------
  // 1) Charger la session
  // -----------------------------
  useEffect(() => {
    if (!sessionId || !user?.access_token) return;

    const fetchSession = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/sessions/${sessionId}`, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });
        console.log('Données de la session chargée :', response.data);
    
        // Tri par order_index désormais
        const sortedPeriods = (response.data.periods || []).slice().sort((a, b) => a.order_index - b.order_index);
    
        setCurrentSession({
          ...response.data,
          periods: sortedPeriods,
        });
        setDancers(response.data.dancers || []);
        setPeriods(sortedPeriods);
        setSkills(response.data.skills || []);
    
        if (sortedPeriods.length > 0) {
          setCurrentPeriodIndex(0);
        }

        // Trouver la première couleur disponible pas encore utilisée pour un skill
        const usedColors = new Set(response.data.skills?.map(skill => skill.color));
        const firstAvailableColor = skillColors.find(color => !usedColors.has(color)) || skillColors[0];

        setNewSkillColor(firstAvailableColor);
        setColorIndex(skillColors.indexOf(firstAvailableColor)); // Met à jour l'index

      } catch (error) {
        console.error('Erreur lors du chargement de la session :', error);
      }
    };

    fetchSession();
  }, [sessionId, user]);

  // -----------------------------
  // 2) Redessiner selon changements
  // -----------------------------
  useEffect(() => {
    if (viewMode === 'scene') {
      drawScene(); 
    } else {
      drawTrajectory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSession,
    currentPeriodIndex,
    selectedPositionId,
    selectedDancerId,
    viewMode,
    canvasPixelWidth,
    canvasPixelHeight,
  ]);


  useEffect(() => {
    if (isPlaying && periods && periods.length > 0) {
      const firstPeriod = periods[0];
      const lastPeriod = [...periods].reverse().find(period => period.end_time !== null);

      const currentPeriod = periods.find(
        period =>
          currentTime >= period.start_time - 0.01 &&
          currentTime <= period.end_time + 0.01
      );
      if (!currentPeriod) {
        if (currentTime < firstPeriod.start_time || currentTime > lastPeriod.end_time) {
          afficherInterpolationPositions();
        } else {
          lancerAnimationVersProchainePeriode();
        }
      }
    }
  }, [isPlaying, currentTime, periods]);
  

// fonctioon pour stopper l'animation quand on clique qque part sur la timeline 
  function stopAnimation(newTime) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
    console.log("Animation stoppée par clic sur la timeline");
    setIsPlaying(false);

    // Vérifier si newTime est dans une période
    const currentPeriod = periods.find(
      period =>
        newTime >= period.start_time - 0.01 &&
        newTime <= period.end_time + 0.01
    );

    // Si le clic se fait entre deux périodes (aucune période trouvée), on redessine le stage classique
    if (currentPeriod) {
      drawScene();
      return;
    }

    // Sinon, selon l'état de lecture, on affiche l'interpolation ou on lance une nouvelle animation
    if (!isPlaying) {
      afficherInterpolationPositions();
    } else {
      lancerAnimationVersProchainePeriode();
    }
  }

  
  
  

  // ANIMATION


  useEffect(() => {
    if (!periods || periods.length === 0) return;
  
    // Détermine si currentTime se trouve dans l'une des périodes
    const currentPeriod = periods.find(
      (period) =>
        // On ajoute une marge de 10ms pour éviter les erreurs d'arrondi
        currentTime >= period.start_time-0.01 && currentTime <= period.end_time + 0.01
    );
    const inAPeriod = Boolean(currentPeriod);
  
    // Si on est hors période et en mode PAUSE, afficher l'interpolation
    if (!inAPeriod && !isPlaying) {
      afficherInterpolationPositions();
    }
  
    // Détection de transition de l'état précédent
    if (prevInPeriod.current !== inAPeriod) {
      if (prevInPeriod.current && !inAPeriod && isPlaying) {
        //console.log("Transition: sortie de période détectée.");
        lancerAnimationVersProchainePeriode();
      } else if (!prevInPeriod.current && inAPeriod) {
        //console.log("Transition: entrée dans une période.");
        // Lors du passage de hors période à dans période, on dessine la scène complète
        drawScene();
      }
      // Mémoriser l'état courant pour la prochaine comparaison
      prevInPeriod.current = inAPeriod;
    }
  }, [currentTime, periods, isPlaying]);
  
  

  // Gestion de l'animation
const animationFrameRef = useRef(null);
const isPlayingRef = useRef(isPlaying);
useEffect(() => {
  isPlayingRef.current = isPlaying;
  // Si on passe en pause et qu'une animation est en cours, on l'annule et on redessine la scène.
  if (!isPlaying && animationFrameRef.current) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
    //drawScene();
  }
}, [isPlaying]);

function lancerAnimationVersProchainePeriode() {

  console.log("Lancement de l'animation vers la prochaine période. depuis le temps ")

  if (!canvasRef.current || !currentSession || !periods || periods.length === 0) return;
  
  // Déterminer la période précédente et la prochaine période
  const firstPeriod = periods[0];
  let previousPeriod = null;
  let nextPeriod = null;
  for (let i = 0; i < periods.length; i++) {
    if (currentTime < periods[i].start_time) {
      nextPeriod = periods[i];
      previousPeriod = periods[i - 1] || firstPeriod;
      break;
    }
  }
  if (!previousPeriod || !nextPeriod) return;
  
  // Calculer le gap complet entre la fin de la période précédente et le début de la prochaine
  const gapDuration = nextPeriod.start_time - previousPeriod.end_time; // en secondes
  // Calculer le facteur d'interpolation au moment du lancement
  let startFactor = (currentTime - previousPeriod.end_time) / gapDuration;
  startFactor = Math.max(0, Math.min(startFactor, 1)); // s'assurer que startFactor est dans [0,1]
  
  // La durée de l'animation correspond au temps restant (en ms)
  const remainingDurationMs = (1 - startFactor) * gapDuration * 1000;
  const startTimeMs = performance.now();
  
  // Préparer les positions animées et celles statiques
  // Pour chaque position de la période précédente qui a un matching dans la prochaine période,
  // on animera sa transition. Les autres seront affichées en semi-transparent.
  const animatedPositions = [];
  const staticPositions = [];
  previousPeriod.positions?.forEach(prevPos => {
    // Si la position n'est pas assignée à un danseur, on la traite comme statique
    if (!prevPos.dancer_id) {
      staticPositions.push(prevPos);
    } else {
      const nextPos = nextPeriod.positions?.find(pos => pos.dancer_id === prevPos.dancer_id);
      if (nextPos) {
        animatedPositions.push({ prevPos, nextPos });
      } else {
        staticPositions.push(prevPos);
      }
    }
  });
  
  
  function animateFrame(now) {
    // S'assurer que le canvas est toujours monté
    if (!canvasRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }
    // Si l'utilisateur met en pause pendant l'animation, on arrête et on redessine la scène
    if (!isPlayingRef.current) {
      //drawScene();
      return;
    }
    
    const elapsed = now - startTimeMs;
    // Calculer la portion de l'animation écoulée (entre 0 et 1)
    const elapsedRatio = Math.min(elapsed / remainingDurationMs, 1);
    // Le facteur d'interpolation global passe de startFactor à 1 pendant l'animation
    const factor = startFactor + (1 - startFactor) * elapsedRatio;
    
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
    drawGrid(ctx);
    
    // Pour chaque position animée, on calcule la position courante par interpolation linéaire
    animatedPositions.forEach(({ prevPos, nextPos }) => {
      const currentX = prevPos.x + factor * (nextPos.x - prevPos.x);
      const currentY = prevPos.y + factor * (nextPos.y - prevPos.y);
      const tempPos = {
        x: currentX,
        y: currentY,
        dancer_id: prevPos.dancer_id,
        id: `anim-${prevPos.dancer_id}` // id temporaire pour le rendu
      };
      drawPosition(ctx, tempPos);
    });
    
    // Les positions sans correspondance dans la prochaine période sont affichées en semi-transparent
    ctx.save();
    ctx.globalAlpha = 0.5;
    staticPositions.forEach(pos => drawPosition(ctx, pos));
    ctx.restore();
    
    if (elapsedRatio < 1) {
      animationFrameRef.current = requestAnimationFrame(animateFrame);
    } else {
      // Fin de l'animation : on met à jour currentTime et on dessine la scène
      if (audioRef.current) {
        //audioRef.current.currentTime = nextPeriod.start_time;
        setCurrentTime(nextPeriod.start_time);
      }
      //drawScene();
    }
  }
  
  animationFrameRef.current = requestAnimationFrame(animateFrame);
  console.log("Animation lancée en mode PLAY depuis un moment hors période.");
}





function afficherInterpolationPositions() {
  // Vérifier que le canvas, la session et les périodes sont bien définis
  if (!canvasRef.current || !currentSession || !periods || periods.length === 0) return;
  
  const ctx = canvasRef.current.getContext('2d');
  // On vide le canvas
  ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
  // On redessine la grille
  drawGrid(ctx);

  // Cas 1 : avant la première période ou après la dernière période → affiche la première période en semi-transparent
  const firstPeriod = periods[0];
  const lastPeriod = [...periods].reverse().find(period => period.end_time !== null);

  if (currentTime < firstPeriod.start_time) {
    ctx.save();
    ctx.globalAlpha = 0.5; // opacité réduite
    firstPeriod.positions?.forEach(pos => {
      drawPosition(ctx, pos);
    });
    ctx.restore();
    return;
  }
  
  if (currentTime > lastPeriod.end_time) {
    ctx.save();
    ctx.globalAlpha = 0.5; // opacité réduite
    lastPeriod.positions?.forEach(pos => {
      drawPosition(ctx, pos);
    });
    ctx.restore();
    return;
  }
  

  // Cas 2 : le temps courant est entre deux périodes
  // On cherche la période suivante dont le début est supérieur au temps courant
  let previousPeriod = null;
  let nextPeriod = null;
  for (let i = 0; i < periods.length; i++) {
    if (currentTime < periods[i].start_time) {
      nextPeriod = periods[i];
      previousPeriod = periods[i - 1] || firstPeriod;
      break;
    }
  }
  
  // Si pour une raison quelconque on n'a pas trouvé de période suivante, on affiche le premier cas
  if (!previousPeriod || !nextPeriod) {
    ctx.save();
    ctx.globalAlpha = 0.5;
    firstPeriod.positions?.forEach(pos => drawPosition(ctx, pos));
    ctx.restore();
    return;
  }
  
  // Calcul du facteur d'interpolation basé sur la position temporelle dans le gap entre la fin de la période précédente et le début de la suivante
  const gapDuration = nextPeriod.start_time - previousPeriod.end_time;
  const t = (currentTime - previousPeriod.end_time) / gapDuration;

  // Pour chaque position de la période précédente :
  // - Si le même danseur a aussi une position dans la période suivante, on calcule une interpolation linéaire.
  // - Sinon, on affiche la position précédente en semi-transparent.
  previousPeriod.positions?.forEach(prevPos => {
    // Si la position n'est pas assignée à un danseur, on l'affiche en semi-transparent sans interpolation
    if (!prevPos.dancer_id) {
      ctx.save();
      ctx.globalAlpha = 0.5;
      drawPosition(ctx, prevPos);
      ctx.restore();
    } else {
      const nextPos = nextPeriod.positions?.find(pos => pos.dancer_id === prevPos.dancer_id);
      if (nextPos) {
        // Calcul de l'interpolation linéaire
        const interpX = prevPos.x + t * (nextPos.x - prevPos.x);
        const interpY = prevPos.y + t * (nextPos.y - prevPos.y);
        const interpPos = { ...prevPos, x: interpX, y: interpY };
        drawPosition(ctx, interpPos);
      } else {
        ctx.save();
        ctx.globalAlpha = 0.5;
        drawPosition(ctx, prevPos);
        ctx.restore();
      }
    }
  });
  
}


  // -----------------------------
  // 3) Fonctions de dessin - SCENE
  // -----------------------------
  function drawScene() {
    if (!canvasRef.current || !currentSession) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);

    drawGrid(ctx);

    const currentPeriod = currentSession.periods?.[currentPeriodIndex];
    if (!currentPeriod) return;

    currentPeriod.positions?.forEach((pos) => drawPosition(ctx, pos));
  }

  function drawGrid(ctx) {

    if (!periods || periods.length === 0) {
      ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
      return;
    }

    // On dessine le quadrillage sur l'espace réel du canvas (pixel)
    ctx.lineWidth = 1;

    // Lignes centrales
    const centerX = canvasPixelWidth / 2;
    const centerY = canvasPixelHeight / 2;

    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, canvasPixelHeight);
    ctx.moveTo(0, centerY);
    ctx.lineTo(canvasPixelWidth, centerY);
    ctx.strokeStyle = 'black';
    ctx.stroke();

    // Lignes en pointillés
    ctx.beginPath();
    const cellSize = canvasPixelWidth / 28; // 28 colonnes
    const numCellsHeight = Math.floor(canvasPixelHeight / cellSize);

    // vertical
    for (let i = 1; i < 28 / 2; i++) {
      ctx.moveTo(centerX + i * cellSize, 0);
      ctx.lineTo(centerX + i * cellSize, canvasPixelHeight);
      ctx.moveTo(centerX - i * cellSize, 0);
      ctx.lineTo(centerX - i * cellSize, canvasPixelHeight);
    }
    // horizontal
    for (let i = 1; i < numCellsHeight / 2; i++) {
      ctx.moveTo(0, centerY + i * cellSize);
      ctx.lineTo(canvasPixelWidth, centerY + i * cellSize);
      ctx.moveTo(0, centerY - i * cellSize);
      ctx.lineTo(canvasPixelWidth, centerY - i * cellSize);
    }
    ctx.strokeStyle = 'gray';
    ctx.setLineDash([2, 2]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /**
   * Dessine une position en tenant compte du scaling
   */
  function drawPosition(ctx, pos) {
    const scaledX = (pos.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
    const scaledY = (pos.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;
    const r = 6;
    const color = pos.dancer_id ? 'blue' : 'gray';
  
    // 1) Cercle principal
    ctx.beginPath();
    ctx.arc(scaledX, scaledY, r, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  
    // Si sélectionné
    if (pos.id === selectedPositionId) {
      ctx.save();
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    }
  
    // 2) Nom du dancer
    const dancer = dancers.find((d) => d.id === pos.dancer_id);
    const label = dancer?.name || `pos:${pos.id}`;
    ctx.fillStyle = 'black';
    ctx.font = '13px sans-serif';
    ctx.fillText(label, scaledX + r + 3, scaledY + r + 3);
  
    // 3) Afficher les skills sous forme d'étoiles
    if (pos.skills && pos.skills.length > 0) {
      const startX = scaledX + 7;
      const startY = scaledY - 7;
      pos.skills.forEach((sk, i) => {
        const offset = i * 12;
        ctx.fillStyle = sk.color || '#000';
        ctx.font = '12px sans-serif';
        ctx.fillText('★', startX + offset, startY);
      });
    }
  }

  // -----------------------------
  // 4) Vue "Trajectory"
  // -----------------------------
  function drawTrajectory() {

    if (!selectedDancerId) {
      if (dancers && dancers.length > 0) {
        setSelectedDancerId(dancers[0].id);
      } else { 
        return;
      }
    }
  
    if (!canvasRef.current || !currentSession) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
  
    drawGrid(ctx);
  
    let dancerPositions = [];
    periods.forEach((period) => {
      const pos = period.positions?.find((p) => p.dancer_id === selectedDancerId);
      if (pos) {
        // On ajoute start_time pour permettre le tri
        dancerPositions.push({ ...pos, periodName: period.name, start_time: period.start_time });
      }
    });
  
    // Trier les positions par ordre de start_time
    dancerPositions.sort((a, b) => a.order_index - b.order_index);
  
    if (dancerPositions.length < 2) {
      dancerPositions.forEach((pos) => drawTrajectoryPosition(ctx, pos));
      return;
    }
  
    for (let i = 0; i < dancerPositions.length; i++) {
      const pos = dancerPositions[i];
      drawTrajectoryPosition(ctx, pos);
  
      if (i < dancerPositions.length - 1) {
        const nextPos = dancerPositions[i + 1];
        drawArrow(ctx, pos, nextPos);
      }
    }
  }
  
  function drawTrajectoryPosition(ctx, pos) {
    // Scale
    const scaledX = (pos.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
    const scaledY = (pos.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;

    const r = 6;
    ctx.beginPath();
    ctx.arc(scaledX, scaledY, r, 0, 2 * Math.PI);
    ctx.fillStyle = 'blue';
    ctx.fill();

    ctx.fillStyle = 'black';
    ctx.font = '13px sans-serif';
    const selectedDancer = dancers.find((d) => d.id === selectedDancerId);
    const dancerName = selectedDancer ? selectedDancer.name : '???';

    ctx.fillText(
      `${dancerName} (Période: ${pos.periodName})`,
      scaledX + r + 3,
      scaledY + r + 3
    );
  }

  function drawArrow(ctx, pos1, pos2) {
    // pos1, pos2 dans le référentiel 0..1045 / 0..665
    const x1 = (pos1.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
    const y1 = (pos1.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;
    const x2 = (pos2.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
    const y2 = (pos2.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;

    ctx.strokeStyle = '#333';
    ctx.fillStyle = '#333';
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    const headlen = 12;
    const angle = Math.atan2(y2 - y1, x2 - x1);

    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(
      x2 - headlen * Math.cos(angle - Math.PI / 6),
      y2 - headlen * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      x2 - headlen * Math.cos(angle + Math.PI / 6),
      y2 - headlen * Math.sin(angle + Math.PI / 6)
    );
    ctx.lineTo(x2, y2);
    ctx.fill();
  }

  // -----------------------------
  // 5) Events souris - "scene" mode
  // -----------------------------
  const onCanvasMouseDown = (e) => {
    if (viewMode !== 'scene' || !currentSession) return;
    if (e.button !== 0) return; // clic gauche uniquement
    if (contextMenuPosition?.visible) return; // si menu contextuel ouvert, on ignore

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Si on est en mode "circle", on ajoute directement une position
    if (cursorMode === 'circle') {
      const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;
      addPositionAt(normX, normY);
      return; 
    }

    // Si on est en mode "star", on tente d'assigner un skill à la position cliquée
    if (cursorMode === 'star') {
      // On retrouve la position existante la plus proche (si < 8px)
      const period = currentSession.periods[currentPeriodIndex];
      const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

      const clickedPos = period.positions.find((pos) => {
        const dx = pos.x - normX;
        const dy = pos.y - normY;
        return Math.sqrt(dx * dx + dy * dy) < 8;
      });

      if (clickedPos && selectedSkillId) {
        // Associe le skill sélectionné à la position
        associateSkillToPosition(clickedPos.id, selectedSkillId);
      }
      return;
    }

    // Si on est en mode "trash", on supprime la position cliquée
    if (cursorMode === 'trash') {
      const period = currentSession.periods[currentPeriodIndex];
      const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

      // Recherche la position la plus proche dans un rayon de 8px
      const clickedPos = period.positions.find((pos) => {
        const dx = pos.x - normX;
        const dy = pos.y - normY;
        return Math.sqrt(dx * dx + dy * dy) < 8;
      });

      if (clickedPos) {
        // Appel de la fonction pour supprimer la position via l'API
        deletePosition(clickedPos.id);
      }
      return;
    }

    // --- Sinon : comportement d’origine ("default")
    const period = currentSession.periods?.[currentPeriodIndex];
    if (!period) return;

    // Chercher la position proche
    const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
    const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;
    const clickedPos = period.positions.find((pos) => {
      const dx = pos.x - normX;
      const dy = pos.y - normY;
      return Math.sqrt(dx * dx + dy * dy) < 8;
    });

    setPendingClickInfo({
      x: clickX,
      y: clickY,
      clickedPosId: clickedPos?.id || null,
      wasSelectedBefore: clickedPos?.id === selectedPositionId,
      isPotentialDragForSelected: !clickedPos && !!selectedPositionId,
    });

    setIsDragging(false);
    setDraggedPosId(null);
  };

  const onCanvasMouseMove = (e) => {
    if (viewMode !== 'scene' || !currentSession) return;

    // Si on est en mode "circle" ou "star", on ne gère pas le drag ici.
    // => On conserve le comportement "drag" UNIQUEMENT en mode "default".
    if (cursorMode !== 'default') return;

    if (!pendingClickInfo) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const moveX = e.clientX - rect.left;
    const moveY = e.clientY - rect.top;

    if (isDragging && draggedPosId) {
      const dx = moveX - dragStart.x;
      const dy = moveY - dragStart.y;

      const updatedSession = { ...currentSession };
      const period = updatedSession.periods[currentPeriodIndex];
      const posIndex = period.positions.findIndex((p) => p.id === draggedPosId);

      if (posIndex !== -1) {
        const normDX = (dx / canvasPixelWidth) * CANVAS_BASE_WIDTH;
        const normDY = (dy / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

        period.positions[posIndex].x += normDX;
        period.positions[posIndex].y += normDY;
        setCurrentSession(updatedSession);
      }

      setDragStart({ x: moveX, y: moveY });
      return;
    }

    const dist = Math.hypot(moveX - pendingClickInfo.x, moveY - pendingClickInfo.y);
    if (dist > THRESHOLD) {
      const posIdToDrag = pendingClickInfo.clickedPosId || selectedPositionId;
      if (posIdToDrag) {
        setIsDragging(true);
        setDraggedPosId(posIdToDrag);
        setDragStart({ x: moveX, y: moveY });
      }
    }
  };

  const onCanvasMouseUp = async (e) => {
    if (viewMode !== 'scene' || !currentSession) return;
    if (!pendingClickInfo) return;
    
    // Idem : on ne gère le "drop" que si on est en mode default
    if (cursorMode !== 'default') {
      setPendingClickInfo(null);
      return;
    }

    if (isDragging && draggedPosId) {
      // Fin d'un drag
      setIsDragging(false);

      const rect = canvasRef.current.getBoundingClientRect();
      const upX = e.clientX - rect.left;
      const upY = e.clientY - rect.top;

      // Snap to grid
      const cellSize = canvasPixelWidth / 28;
      const snapX = Math.round(upX / cellSize) * cellSize;
      const snapY = Math.round(upY / cellSize) * cellSize;

      const normalizedX = (snapX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normalizedY = (snapY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

      try {
        await axios.put(
          `${API_BASE_URL}/sessions/${currentSession.id}/positions/${draggedPosId}`,
          { x: normalizedX, y: normalizedY}
        );

        const updatedSession = { ...currentSession };
        const period = updatedSession.periods[currentPeriodIndex];
        const posIndex = period.positions.findIndex((p) => p.id === draggedPosId);

        if (posIndex !== -1) {
          period.positions[posIndex].x = normalizedX;
          period.positions[posIndex].y = normalizedY;
          setCurrentSession(updatedSession);
        }
      } catch (error) {
        console.error('Error updating position:', error);
      }

      setDraggedPosId(null);
    } else {
      // Simple clic
      const period = currentSession.periods[currentPeriodIndex];
      const { clickedPosId, wasSelectedBefore } = pendingClickInfo;

      if (clickedPosId) {
        if (wasSelectedBefore) {
          handlePositionSelect(null, null);
        } else {
          const pos = period.positions.find((p) => p.id === clickedPosId);
          handlePositionSelect(clickedPosId, pos?.dancer_id);
        }
      }
    }

    setPendingClickInfo(null);
  };

  const onCanvasContextMenu = (e) => {
    if (viewMode !== 'scene' || !currentSession) return;
    e.preventDefault();
  
    // Utilisation des coordonnées de la fenêtre
    const clickX = e.pageX;
    const clickY = e.pageY;
  
    // Définir la largeur et la hauteur estimée du menu (à ajuster au besoin)
    const menuWidth = 200;
    const menuHeight = 220;
  
    // Limiter pour que le menu ne dépasse pas l'écran
    const limitedX = clickX + menuWidth > window.innerWidth
      ? window.innerWidth - menuWidth - 5
      : clickX;
    const limitedY = clickY + menuHeight > window.innerHeight
      ? window.innerHeight - menuHeight - 5
      : clickY;
  
    const period = currentSession.periods?.[currentPeriodIndex];
    if (!period) return;
  
    // Recherche de la position cliquée (reste identique)
    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const normX = (offsetX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
    const normY = (offsetY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;
    const clickedPos = period.positions.find((pos) => {
      const dx = pos.x - normX;
      const dy = pos.y - normY;
      return Math.sqrt(dx * dx + dy * dy) < 8;
    });
  
    const options = [];
  
    if (!clickedPos) {
      options.push(
        {
          label: 'Ajouter une position',
          onClick: () => addPositionAt(normX, normY),
        },
        {
          label: 'Dissocier tous les danseurs',
          onClick: () => dissociateAllDancers(),
        }
      );
    } else {
      if (clickedPos.dancer_id) {
        options.push(
          {
            label: 'Dissocier le danseur',
            onClick: () => dissociateDancerFromPosition(clickedPos.id),
          },
          {
            label: 'Supprimer cette position',
            onClick: () => deletePosition(clickedPos.id),
          }
        );
      } else {
        const associatedDancerIds = period.positions
          .map((p) => p.dancer_id)
          .filter(Boolean);
        const availableDancers = dancers.filter(
          (d) => !associatedDancerIds.includes(d.id)
        );
        options.push(
          {
            label: 'Associer un danseur',
            subMenu: availableDancers.map((dancer) => ({
              label: dancer.name,
              onClick: () => associateDancerToPosition(clickedPos.id, dancer.id),
            })),
          },
          {
            label: 'Supprimer cette position',
            onClick: () => deletePosition(clickedPos.id),
          }
        );
      }
  
      const currentSkills = clickedPos.skills || [];
      const subMenuAssocierSkill = (skills || [])
        .filter((sk) => !currentSkills.some((posSk) => posSk.id === sk.id))
        .map((sk) => ({
          label: sk.name,
          onClick: () => associateSkillToPosition(clickedPos.id, sk.id),
        }));
  
      const subMenuDissocierSkill = currentSkills.map((sk) => ({
        label: sk.name,
        onClick: () => removeSkillFromPosition(clickedPos.id, sk.id),
      }));
  
      if (subMenuAssocierSkill.length > 0) {
        options.push({
          label: 'Associer un skill',
          subMenu: subMenuAssocierSkill,
        });
      }
      if (subMenuDissocierSkill.length > 0) {
        options.push({
          label: 'Dissocier un skill',
          subMenu: subMenuDissocierSkill,
        });
      }
    }
  
    // Ouvrir le menu contextuel avec useFixed: true pour que le menu puisse apparaître en dehors du canvas
    setContextMenuPosition({
      x: limitedX,
      y: limitedY,
      options,
      visible: true,
      useFixed: true,
    });
  };
  

  function handleDancerRightClick(dancer, e) {
    e.preventDefault();
    
      // Construire les sous-menus
      const subMenuAssocier = (skills || [])
      // On ne propose que les skills qui ne sont pas déjà associés
      .filter((sk) => !dancer.skills.some(ds => ds.id === sk.id))
      // On construit les options
      .map((sk) => {
        return {
          label: sk.name,
          onClick: () => {
            // on ne fera rien si c'est disabled (on vérifie côté ContextMenu)
            associateSkillToDancer(dancer.id, sk.id);
          }
        }
      });
    
      const subMenuDissocier = (dancer.skills || []).map((sk) => ({
        label: sk.name,
        onClick: () => {
          dissociateSkillFromDancer(dancer.id, sk.id);
        },
      }));
    
      // Construction de la liste d'options
      const options = [
        {
          label: 'Supprimer le danseur',
          onClick: () => {
            deleteDancer(dancer.id);
          },
        },
        {
          label: 'Associer un skill',
          subMenu: subMenuAssocier, 
        },
      ];
    
      // On n'ajoute "Dissocier un skill" que si le danseur a des skills
      if (dancer.skills && dancer.skills.length > 0) {
        options.push({
          label: 'Dissocier un skill',
          subMenu: subMenuDissocier,
        });
      }
    
      // Calculer position X/Y
      const clickX = e.pageX;
      const clickY = e.pageY;
    
      // On peut éventuellement limiter l'emplacement pour ne pas dépasser l'écran
      // (comme on le fait dans onCanvasContextMenu). Ex:
      // const menuWidth = 200;
      // const limitedX = Math.min(clickX, windowSize.width - menuWidth);
      // const limitedY = Math.min(clickY, windowSize.height - 220);
    
      setDancerContextMenu({
        x: clickX,
        y: clickY,
        visible: true,
        dancerId: dancer.id,
        options,
        useFixed: true,  // <--- on utilise position: fixed
      });
    }

  // -----------------------------
  // 6) Associer / Dissocier
  // -----------------------------
  async function associateDancerToPosition(positionId, dancerId) {
    try {
      await axios.put(
        `${API_BASE_URL}/sessions/${currentSession.id}/positions/${positionId}`,
        { dancer_id: dancerId }
      );
      const updatedSession = { ...currentSession };
      const period = updatedSession.periods[currentPeriodIndex];
      const position = period.positions.find((p) => p.id === positionId);
      if (position) position.dancer_id = dancerId;
      setCurrentSession(updatedSession);
    } catch (error) {
      console.error('Erreur d\'association :', error);
    }
  }

  async function dissociateDancerFromPosition(positionId) {
    try {
      await axios.put(
        `${API_BASE_URL}/sessions/${currentSession.id}/positions/${positionId}`,
        { dancer_id: null }
      );
      const updatedSession = { ...currentSession };
      const period = updatedSession.periods[currentPeriodIndex];
      const position = period.positions.find((p) => p.id === positionId);
      if (position) position.dancer_id = null;
      setCurrentSession(updatedSession);
    } catch (error) {
      console.error('Erreur de dissociation :', error);
    }
  }

  async function dissociateAllDancers() {
    try {
      await axios.put(
        `${API_BASE_URL}/sessions/${currentSession.id}/periods/${currentSession.periods[currentPeriodIndex].id}/dissociate_all_dancers`
      );
      const updatedSession = { ...currentSession };
      const periodIndex = currentPeriodIndex;
      updatedSession.periods[periodIndex].positions =
        updatedSession.periods[periodIndex].positions.map((pos) => ({
          ...pos,
          dancer_id: null,
        }));
      setCurrentSession(updatedSession);
    } catch (error) {
      console.error('Erreur de dissociation globale :', error);
    }
  }

  async function associateSkillToPosition(posId, skillId) {
    try {
      const res = await PositionService.associateSkill(currentSession.id, posId, skillId);
      // res.data contient la Position mise à jour => met à jour le state local
      const updatedPosition = res.data;
  
      // On va mettre à jour le currentSession => 
      // remplacer la position dans la période courante
      const updatedSession = { ...currentSession };
      const period = updatedSession.periods[currentPeriodIndex];
      const posIndex = period.positions.findIndex(p => p.id === updatedPosition.id);
      if (posIndex !== -1) {
        period.positions[posIndex] = updatedPosition; // replace old with new
      }
  
      setCurrentSession(updatedSession);
    } catch (err) {
      console.error("Erreur association skill sur position :", err);
    }
  }
  
  async function removeSkillFromPosition(posId, skillId) {
    try {
      const res = await PositionService.removeSkill(currentSession.id, posId, skillId);
      const updatedPosition = res.data;
  
      const updatedSession = { ...currentSession };
      const period = updatedSession.periods[currentPeriodIndex];
      const posIndex = period.positions.findIndex(p => p.id === updatedPosition.id);
      if (posIndex !== -1) {
        period.positions[posIndex] = updatedPosition;
      }
  
      setCurrentSession(updatedSession);
    } catch (err) {
      console.error("Erreur dissociation skill sur position :", err);
    }
  }

  // -----------------------------
  // 7) Sélections
  // -----------------------------

  function handlePositionSelect(posId, dancerId) {
    setSelectedPositionId(posId);
    setSelectedDancerId(dancerId || null);
  }

  function handleDancerSelect(dancerId) {
    if (cursorMode === 'star') {
      // Si une skill est sélectionnée => associer la skill au danseur
      if (selectedSkillId) {
        associateSkillToDancer(dancerId, selectedSkillId);
      }
      // Sinon, star "creuse" => pas d'action
      return;
    }

    // Sinon, mode normal => sélection
    setSelectedDancerId(dancerId);
    const period = currentSession?.periods?.[currentPeriodIndex];
    if (!period) return;
    const pos = period.positions.find((p) => p.dancer_id === dancerId);
    setSelectedPositionId(pos?.id || null);
  }

  useEffect(() => {
    if (selectedDancerId && periods.length > 0) {
      const newPeriod = periods[currentPeriodIndex];
      if (newPeriod) {
        const newPosition = newPeriod.positions.find(pos => pos.dancer_id === selectedDancerId);
        setSelectedPositionId(newPosition ? newPosition.id : null);
      }
    }
  }, [currentPeriodIndex, selectedDancerId, periods]);
  

  // -----------------------------
  // 8) Dancers & Skills
  // -----------------------------
  async function addDancer() {
    if (!newDancerName.trim() || !currentSession) return;
    try {
      const res = await DancerService.createDancer(currentSession.id, {
        name: newDancerName,
      });
      const updatedSession = {
        ...currentSession,
        dancers: [...currentSession.dancers, res.data],
      };
      setCurrentSession(updatedSession);
      setDancers([...dancers, res.data]);
      setNewDancerName('');
    } catch (err) {
      console.error('Erreur création danseur :', err);
    }
  }

  async function deleteDancer(dancerId) {
    if (!window.confirm('Supprimer ce danseur ?') || !currentSession) return;
    try {
      await DancerService.deleteDancer(currentSession.id, dancerId);
      const updatedSession = {
        ...currentSession,
        dancers: currentSession.dancers.filter((d) => d.id !== dancerId),
        periods: currentSession.periods.map((p) => ({
          ...p,
          positions: p.positions.map((pos) =>
            pos.dancer_id === dancerId ? { ...pos, dancer_id: null } : pos
          ),
        })),
      };
      setCurrentSession(updatedSession);
      setDancers(updatedSession.dancers);
    } catch (err) {
      console.error('Erreur suppression danseur :', err);
    }
  }

  async function addSkill() {
    if (!newSkillName.trim() || !currentSession) return;
    
    try {
      const res = await SkillService.createSkill(currentSession.id, {
        name: newSkillName,
        color: newSkillColor, // Utilise la couleur choisie par l'utilisateur
      });
  
      const updatedSession = {
        ...currentSession,
        skills: [...currentSession.skills, res.data],
      };
  
      setCurrentSession(updatedSession);
      setSkills([...skills, res.data]);
      setNewSkillName('');
  
      // Passer à la prochaine couleur de la liste si l'utilisateur n'a pas choisi manuellement
      const nextIndex = (colorIndex + 1) % skillColors.length;
      setColorIndex(nextIndex);
      setNewSkillColor(skillColors[nextIndex]); // Met à jour la couleur par défaut
    } catch (err) {
      console.error('Erreur création skill :', err);
    }
  }

  async function deleteSkill(skillId) {
    if (!window.confirm('Supprimer ce skill ?') || !currentSession) return;
    try {
        await SkillService.deleteSkill(currentSession.id, skillId);
        const updatedSession = {
            ...currentSession,
            skills: currentSession.skills.filter((s) => s.id !== skillId),
            dancers: currentSession.dancers.map((d) => ({
                ...d,
                skills: d.skills.filter((sk) => sk.id !== skillId),
            })),
            periods: currentSession.periods.map((p) => ({
                ...p,
                positions: p.positions.map((pos) => ({
                    ...pos,
                    skills: pos.skills.filter((sk) => sk.id !== skillId),
                })),
            })),
        };

        setCurrentSession(updatedSession);
        setSkills(updatedSession.skills);

    } catch (err) {
        console.error('Erreur suppression skill :', err);
    }
}


async function associateSkillToDancer(dancerId, skillId) {
  try {
    const res = await DancerService.associateSkill(currentSession.id, dancerId, skillId);
    // Mettre à jour le state local currentSession, dancers, etc.
    const updatedDancer = res.data;
    const updatedDancers = dancers.map((d) => (d.id === dancerId ? updatedDancer : d));
    setDancers(updatedDancers);

    // Idem, il faut mettre à jour currentSession pour que l'UI reste sync
    const updatedSession = {
      ...currentSession,
      dancers: updatedDancers,
    };
    setCurrentSession(updatedSession);

  } catch (err) {
    console.error("Erreur association skill:", err);
  }
}

async function dissociateSkillFromDancer(dancerId, skillId) {
  try {
    const res = await DancerService.removeSkill(currentSession.id, dancerId, skillId);
    const updatedDancer = res.data;
    const updatedDancers = dancers.map((d) => (d.id === dancerId ? updatedDancer : d));
    setDancers(updatedDancers);

    const updatedSession = {
      ...currentSession,
      dancers: updatedDancers,
    };
    setCurrentSession(updatedSession);

  } catch (err) {
    console.error("Erreur dissociation skill:", err);
  }
}


  // -----------------------------
  // 9) Positions
  // -----------------------------
  async function deletePosition(positionId) {
    if (!currentSession) return;
    try {
      await axios.delete(`${API_BASE_URL}/sessions/${currentSession.id}/positions/${positionId}`);
      const updatedSession = { ...currentSession };
      const periodIndex = updatedSession.periods.findIndex(
        (p) => p.id === currentSession.periods[currentPeriodIndex].id
      );
      updatedSession.periods[periodIndex].positions = updatedSession.periods[periodIndex].positions.filter(
        (p) => p.id !== positionId
      );
      setCurrentSession(updatedSession);
    } catch (error) {
      console.error('Error deleting position:', error);
    }
  }

  /**
   * Ajoute une position en (normX, normY) référentiel 0..1045/0..665
   * On applique ensuite un snap éventuel si besoin ou direct
   */
  async function addPositionAt(normX, normY) {
    if (!currentSession) return;
    const currentPeriod = currentSession.periods[currentPeriodIndex];
    if (!currentPeriod) return;

    // Snap: on convertit (normX, normY) en pixel, on snap, puis on re-normalise
    const pixelX = (normX / CANVAS_BASE_WIDTH) * canvasPixelWidth;
    const pixelY = (normY / CANVAS_BASE_HEIGHT) * canvasPixelHeight;

    const cellSize = canvasPixelWidth / 28;
    const snapX = Math.round(pixelX / cellSize) * cellSize;
    const snapY = Math.round(pixelY / cellSize) * cellSize;

    const finalNormX = (snapX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
    const finalNormY = (snapY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

    try {
      const res = await axios.post(
        `${API_BASE_URL}/sessions/${currentSession.id}/periods/${currentPeriod.id}/positions`,
        { x: finalNormX, y: finalNormY }
      );
      const updatedSession = { ...currentSession };
      const periodIndex = updatedSession.periods.findIndex((p) => p.id === currentPeriod.id);
      updatedSession.periods[periodIndex].positions.push(res.data);
      setCurrentSession(updatedSession);
    } catch (error) {
      console.error('Error creating position:', error);
    }
  }

  // -----------------------------
  // 10) Periods
  // -----------------------------
  
  // formater le timestamp affiché dans la barre latterale 
  function formatTimestamp(ts) {
    if (ts === null || ts === undefined) return '--:--';
    const minutes = Math.floor(ts / 60);
    const seconds = Math.floor(ts % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  async function addPeriod() {
    if (!newPeriodName.trim() || !currentSession) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/sessions/${currentSession.id}/periods`,
        { name: newPeriodName }
      );
  
      // Ajout de la nouvelle période et tri par order_index désormais
      const updatedPeriods = [...periods, res.data].slice().sort((a, b) => a.order_index - b.order_index);
  
      // Trouver l'index de la nouvelle période dans la liste triée
      const newIndex = updatedPeriods.findIndex(p => p.id === res.data.id);
  
      // Mise à jour des états
      setCurrentSession({
        ...currentSession,
        periods: updatedPeriods,
      });
      setPeriods(updatedPeriods);
      setCurrentPeriodIndex(newIndex); // Sélectionne la nouvelle période
      setNewPeriodName('');
    } catch (error) {
      console.error('Error adding period:', error);
    }
  }
  

  async function deletePeriod(periodId) {
    if (!currentSession) return;
    const period = currentSession.periods.find((p) => p.id === periodId);
    if (!period) return;
    if (!window.confirm(`Supprimer la période "${period.name}" ?`)) return;

    try {
      await axios.delete(`${API_BASE_URL}/sessions/${currentSession.id}/periods/${periodId}`);
      const updatedSession = {
        ...currentSession,
        periods: currentSession.periods.filter((p) => p.id !== periodId),
      };
      setCurrentSession(updatedSession);
      setPeriods(updatedSession.periods);
      if (currentPeriodIndex >= updatedSession.periods.length) {
        setCurrentPeriodIndex(Math.max(0, updatedSession.periods.length - 1));
      }
    } catch (error) {
      console.error('Error deleting period:', error);
    }
  }

  // -----------------------------
  // 11) Optimization
  // -----------------------------
  async function runOptimization() {
    if (!currentSession) return;
  
    setIsOptimizing(true);  // Active l'overlay
    setElapsedTime(0);      // Reset le timer
  
    const startTime = Date.now();
    const timeLimit = 30; // secondes (peut être modifié)
  
    // Timer qui met à jour `elapsedTime` toutes les 100 ms
    const timerInterval = setInterval(() => {
      setElapsedTime(((Date.now() - startTime) / 1000).toFixed(2));
    }, 100);
  
    try {
      console.log(forcedPosition);
      const resp = await axios.post(
        `${API_BASE_URL}/sessions/${currentSession.id}/optimize`,
        { time_limit: timeLimit, initial_temp: 1000, cooling_rate: 0.995, forcedPosition: forcedPosition }
      );
      
    
      const updatedSessionResponse = await axios.get(
        `${API_BASE_URL}/sessions/${currentSession.id}`,
        { headers: { Authorization: `Bearer ${user.access_token}` } }
      );
      const updatedSession = updatedSessionResponse.data;
    
      // Tri des périodes par order_index désormais
      const sortedPeriods = (updatedSession.periods || []).slice().sort((a, b) => a.order_index - b.order_index);
    
    
      setCurrentSession({
        ...updatedSession,
        periods: sortedPeriods,
      });
      setPeriods(sortedPeriods);
      setDancers(updatedSession.dancers);
      setSkills(updatedSession.skills);
    
      // Reste du code...
    } catch (err) {
      console.error('Erreur lors de l\'optimisation :', err);
    } finally {
      clearInterval(timerInterval);
      setIsOptimizing(false);
    }
    
  }
  

  // -----------------------------
  // 12) Switch de vue
  // -----------------------------
  function toggleViewMode() {
    setViewMode((prev) => (prev === 'scene' ? 'trajectory' : 'scene'));
  }

  // ==============================
  // UPLOAD MUSIQUE
  // ==============================

  // Empêcher le comportement par défaut pour autoriser le drop
  function handleMusicDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setMusicIsDragging(true);
  }
 
  function handleMusicDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setMusicIsDragging(false);
  }
 
  async function handleMusicDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setMusicIsDragging(false);
 
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      await handleMusicFile(file);
    }
  }
 
  // Ouverture du sélecteur de fichier via un click
  function handleMusicClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }
 
  // Quand l'utilisateur choisit un fichier via <input type="file" />
  async function handleMusicFileInputChange(e) {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      await handleMusicFile(file);
    }
  }
 
  // Vérifie la validité (taille, type, etc.) puis envoie la musique au backend
  async function handleMusicFile(file) {
    console.log("Fichier audio sélectionné :", file);
  
    if (!file.type.startsWith("audio/")) {
      alert("Fichier non supporté. Merci de sélectionner un fichier audio.");
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) {
      alert("Fichier trop volumineux. Maximum 10 Mo.");
      return;
    }
  
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("session_id", currentSession.id);
  
      const res = await axios.post(`${API_BASE_URL}/musics`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${user.access_token}`,
        },
      });
  
      console.log("Upload réussi:", res.data);
      alert("Musique uploadée avec succès !");
  
      // 🔥 Rafraîchir la session pour charger la nouvelle musique
      const updatedSessionResponse = await axios.get(`${API_BASE_URL}/sessions/${currentSession.id}`, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
  
      setCurrentSession(updatedSessionResponse.data);
      setPeriods(updatedSessionResponse.data.periods);
  
    } catch (err) {
      console.error("Erreur upload:", err);
      alert("Erreur lors de l'upload de la musique.");
    }
  }
  
  // ==============================
  // LECTURE dans StageView.jsx
  // ==============================


  // Ajoutez ce useEffect pour stopper la musique lors du démontage du composant
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        console.log("Audio stoppé lors du démontage du composant.");
      }
    };
  }, []);

  // Synchroniser currentTime avec la progression naturelle de l'audio
  useEffect(() => {
    if (audioRef.current) {
      const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current.currentTime);
      };
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      return () => {
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [audioRef.current]);

  useEffect(() => {
    if (currentSession && currentSession.musics && currentSession.musics.length > 0) {
      const musicUrl = `${API_BASE_URL}/musics/${currentSession.musics[0].id}/file`;
      // Mettre à jour la source uniquement si elle a changé
      if (!audioRef.current) {
        audioRef.current = new Audio(musicUrl);
        audioRef.current.crossOrigin = 'anonymous';
      } else if (audioRef.current.src !== musicUrl) {
        audioRef.current.src = musicUrl;
        audioRef.current.crossOrigin = 'anonymous';
      }
    }
  }, [currentSession]);
  

  // Contrôle de la lecture en fonction de isPlaying
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        // Laisse l'audio avancer naturellement
        audioRef.current.play().catch((err) => {
          console.error("Erreur lors du play :", err);
        });
      } else {
        audioRef.current.pause();
        console.log("Pause");
      }
    }
  }, [isPlaying]);

  // si on est entre le début/fin d'une periode alors on la selectionne
  useEffect(() => {
    if (!periods || periods.length === 0) return;
    // Ne considérer que les périodes avec start_time et end_time définis
    const index = periods.findIndex(period =>
      period.start_time != null &&
      period.end_time != null &&
      currentTime >= period.start_time &&
      currentTime <= period.end_time
    );
    if (index !== -1 && index !== currentPeriodIndex) {
      setCurrentPeriodIndex(index);
    }
  }, [currentTime, periods, currentPeriodIndex]);
  


  // ==============================
  // CONTROL BAR 
  // ==============================

  // Placez ce useEffect au niveau supérieur de votre composant (avec les autres useEffect)
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  
  // -----------------------------
  // PDF DOWNLOAD
  // -----------------------------

  const handleDownloadPdf = () => {
    // Fonction utilitaire pour convertir un hex en [r, g, b]
    const hexToRgb = (hex) => {
      hex = hex.replace(/^#/, "");
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }
      const bigint = parseInt(hex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return [r, g, b];
    };
  
    // Création du document avec compression activée
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
      compress: true,
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40; // marge de base
  
    // Fonction pour dessiner le cadre sur la page actuelle
    const drawPageFrame = () => {
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(2);
      doc.rect(margin / 2, margin / 2, pageWidth - margin, pageHeight - margin);
    };
  
    // Fonction pour dessiner le logo dans le coin supérieur droit
    const drawLogoTopRight = () => {
      doc.setFont("Shirkhand", "normal"); // Assurez-vous que cette police soit chargée
      doc.setFontSize(20);
      doc.setTextColor(255, 71, 126); // couleur #ff477e
      doc.text("Dansy", pageWidth - margin, margin + 20, { align: "right" });
      doc.setTextColor(0, 0, 0);
    };
  
    // ============================
    // Page 1 : Couverture améliorée
    // ============================
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
    drawPageFrame();
    drawLogoTopRight();
  
    doc.setFont("helvetica", "bold");
    doc.setFontSize(36);
    doc.text("Présentation de la chorégraphie", pageWidth / 2, pageHeight / 2 - 80, { align: "center" });
    doc.setFontSize(20);
    doc.setFont("helvetica", "normal");
    doc.text(`Session : ${currentSession.name}`, pageWidth / 2, pageHeight / 2 - 40, { align: "center" });
    doc.text(`Danseurs : ${dancers.length}`, pageWidth / 2, pageHeight / 2 - 10, { align: "center" });
    doc.text(`Périodes : ${periods.length}`, pageWidth / 2, pageHeight / 2 + 20, { align: "center" });
    doc.text(`Exporté le : ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight / 2 + 50, { align: "center" });
  
    // ============================
    // Page 2 : Liste des éléments (tableau)
    // ============================
    doc.addPage();
    drawPageFrame();
    drawLogoTopRight();
  
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text("Caractéristiques générales :", pageWidth / 2, margin + 20, { align: "center" });
  
    // Préparation des données pour le tableau
    const periodsList = periods.map(
      (period) =>
        `${period.name} (${formatTimestamp(period.start_time)} > ${formatTimestamp(period.end_time)})`
    );
    const dancersList = dancers.map((dancer) => dancer.name);
    // Pour les skills, on conserve les objets afin de pouvoir afficher le nom et l'étoile
    const skillsArray = skills.map((skill) => skill);
    const maxLength = Math.max(periodsList.length, dancersList.length, skillsArray.length);
    while (periodsList.length < maxLength) periodsList.push("");
    while (dancersList.length < maxLength) dancersList.push("");
    while (skillsArray.length < maxLength) skillsArray.push(null);
  
    const colWidth = (pageWidth - 2 * margin) / 3;
    let startY = 100; // Début après le titre
    const defaultRowHeight = 12;
    const availableHeight = pageHeight - margin - startY;
    let rowHeight = defaultRowHeight;
    if (maxLength * defaultRowHeight > availableHeight) {
      rowHeight = availableHeight / maxLength;
    }
  
    // En-tête du tableau
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Périodes", margin, startY);
    doc.text("Danseurs", margin + colWidth, startY);
    doc.text("Skills", margin + 2 * colWidth, startY);
    startY += rowHeight + 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
  
    // Lignes du tableau
    for (let i = 0; i < maxLength; i++) {
      const currentY = startY + i * rowHeight;
      // Périodes et Danseurs s'affichent normalement
      doc.text(periodsList[i], margin, currentY);
      doc.text(dancersList[i], margin + colWidth, currentY);
  

    
      // Pour Skills, afficher le nom du skill en changeant la couleur du texte
      const skill = skillsArray[i];
      if (skill) {
        // Convertir la couleur hexadécimale en RGB
        const [r, g, b] = hexToRgb(skill.color);
        doc.setTextColor(r, g, b);
        doc.text(skill.name, margin + 2 * colWidth, currentY);
        doc.setTextColor(0, 0, 0); // Réinitialiser la couleur du texte
      }
      }
  
    // ============================
    // Pages pour chaque période (scène)
    // ============================
    periods.forEach((period) => {
      doc.addPage();
      drawPageFrame();
      drawLogoTopRight();
  
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.text(`Période : ${period.name}`, pageWidth / 2, margin + 20, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(14);
      doc.text(
        `De ${formatTimestamp(period.start_time)} à ${formatTimestamp(period.end_time)}`,
        pageWidth / 2,
        margin + 40,
        { align: "center" }
      );
  
      const offscreenCanvas = document.createElement("canvas");
      offscreenCanvas.width = canvasPixelWidth;
      offscreenCanvas.height = canvasPixelHeight;
      const ctx = offscreenCanvas.getContext("2d");
  
      drawGrid(ctx);
      if (period.positions && period.positions.length > 0) {
        period.positions.forEach((pos) => drawPosition(ctx, pos));
      }
  
      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = pageHeight - 2 * margin - 60;
      const imgX = margin;
      const imgY = margin + 60;
      const canvasImage = offscreenCanvas.toDataURL("image/png");
      doc.addImage(canvasImage, "PNG", imgX, imgY, imgWidth, imgHeight);
      doc.setDrawColor(150, 150, 150);
      doc.setLineWidth(1);
      doc.roundedRect(imgX, imgY, imgWidth, imgHeight, 10, 10, "S");
    });
  
    // ============================
    // Dernière page : Page de fermeture centrée verticalement
    // ============================

    doc.addPage();

    // D'abord, dessiner le fond et le cadre
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
    drawPageFrame();

    // Puis dessiner le logo dans le coin supérieur droit
    drawLogoTopRight();

    const closingCenterY = pageHeight / 2;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(36);
    doc.setTextColor(0, 0, 0);
    doc.text("Merci !", pageWidth / 2, closingCenterY - 20, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text("Réalisé sur www.dansy.net", pageWidth / 2, closingCenterY + 10, { align: "center" });
    doc.setTextColor(0, 0, 0);

  
    doc.save(`${currentSession.name || "export"}.pdf`);
  };
  

  // -----------------------------
  // 13) Rendu final
  // -----------------------------
  return (
    <div
      style={{
        display: 'flex',
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Barre latérale */}
      <div
        style={{
          width: `${SIDEBAR_WIDTH}px`,
          background: "#f2f2f2",
          borderRight: '1px solid #d1d1d6',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Zone fixe contenant le logo et le bouton */}
        <div 
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 20px",
            background: "linear-gradient(135deg, #ffd6dd,rgb(255, 248, 249))",
            borderBottom: "1px solid #d1d1d6",
            position: "sticky",
            top: 0,
            zIndex: 100,
            height: "40px",  
          }}
          className="nav-bar"
        >

          {/* Zone gauche (Logo) */}
          <div 
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: "flex-start",
              paddingRight: "10px",
              height: "100%",
              position: "relative",
              cursor: "pointer",
            }}
            className="left-section"
            onClick={() => navigate("/")}
          >
            <span 
              style={{
                fontSize: "1.7rem",
                fontFamily: "'Shirkhand', cursive",
                color: "#ff477e",
                fontWeight: "bold",
                transformOrigin: "left",
                transition: "transform 0.2s ease-in-out", // Transition uniquement sur l'agrandissement
              }} 
              className="dansy"
              onMouseEnter={(e) => e.target.style.transform = "scale(1.1)"}
              onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
            >
              Dansy
            </span>
          </div>


          {/* Zone droite (Bouton Mes sessions) */}
          <div 
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: "flex-end",
              paddingLeft: "10px",
              height: "100%",
              position: "relative",
            }}
            className="right-section"
          >
            <button
              onClick={() => navigate('/sessions')}
              style={{
                padding: "6px 30px",
                height: "100%",
                borderRadius: "0px",
                border: "none",
                backgroundColor: "transparent",
                color: "#242323",
                cursor: "pointer",
                fontSize: "0.8rem",
                transition: "transform 0.2s ease-in-out", // Transition uniquement sur l'agrandissement
              }}
              className="retour"
              onMouseEnter={(e) => e.target.style.transform = "scale(1.08)"}
              onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
            >
              Mes sessions
            </button>
          </div>

        </div>
        
        {/* Nom de la session */}
        {currentSession && (
          <div 
            title={currentSession.name}
            style={{
              textAlign: "center",
              fontSize: "1.5rem",
              fontWeight: "bold",
              padding: "6px",
              background: "#e9e9e9",
              borderBottom: "1px solid #d1d1d6",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {currentSession.name}
          </div>
        )}

        {/* Contenu de la barre latérale */}
        <div style={{ padding: '1rem' }}>

          {currentSession && (
            <SectionList
              title="Périodes"
              data={[...periods].sort((a, b) => a.order_index - b.order_index)}
              selectedId={periods[currentPeriodIndex]?.id}
              onSelect={(id) => {
                const newIndex = periods.findIndex((p) => p.id === id);
                setCurrentPeriodIndex(newIndex);
                const selectedPeriod = periods[newIndex];
                if (selectedPeriod && selectedPeriod.start_time != null) {
                  if (audioRef.current) {
                    audioRef.current.currentTime = selectedPeriod.start_time;
                  }
                  setCurrentTime(selectedPeriod.start_time);
                  setIsPlaying(false); // Ajouté pour mettre en pause la lecture
                } else {
                  setCurrentTime(null);
                }
              }}
              
              onAdd={addPeriod}
              addPlaceholder="Nom de la période"
              addValue={newPeriodName}
              setAddValue={setNewPeriodName}
              onDelete={(id) => deletePeriod(id)}
              renderItem={(period) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  {/* Nom de la période à gauche */}
                  <div>{period.name}</div>
                  {/* Timestamp formaté à droite */}
                  <div style={{ marginLeft: 'auto', fontSize: '0.73rem', color: '#555' }}>
                    {formatTimestamp(period.start_time)} &gt; {formatTimestamp(period.end_time)}
                  </div>
                </div>
              )}
            />
          )}




          {currentSession && (
            <SectionList
              title="Danseurs"
              data={dancers}
              selectedId={selectedDancerId}
              onSelect={handleDancerSelect}
              onAdd={addDancer}
              addPlaceholder="Nom du danseur"
              addValue={newDancerName}
              setAddValue={setNewDancerName}
              onDelete={deleteDancer}
              onRightClick={handleDancerRightClick}
              renderItem={(dancer) => (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {/* Nom du dancer à gauche */}
                  <span>{dancer.name}</span>
                  {/* Étoiles alignées à droite pour ses skills */}
                  <span>
                    {dancer.skills?.map((sk) => (
                      <span key={sk.id} style={{ color: sk.color, marginLeft: '4px' }}>★</span>
                    ))}
                  </span>
                </span>
              )}
            />
          )}

          {currentSession && (
            <SectionList
              title="Skills"
              data={skills}
              selectedId={selectedSkillId}
              onSelect={setSelectedSkillId}
              onAdd={addSkill}
              addPlaceholder="Nom du skill"
              addValue={newSkillName}
              setAddValue={setNewSkillName}
              onDelete={deleteSkill}
              extraInput={(
                <input 
                  type="color" 
                  value={newSkillColor} 
                  onChange={(e) => setNewSkillColor(e.target.value)} 
                  style={{borderRadius: '100%', width: '2.5rem', height: '2rem', border: 'none', cursor: 'pointer', marginLeft: '0px', alignItems: 'center' }}
                />
              )}
              renderItem={(skill) => (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: skill.color,
                      display: 'inline-block',
                      marginRight: '8px',
                    }}
                  />
                  {skill.name}
                </span>
              )}
            />

          )}

          <hr
            style={{
              margin: '1rem 0',
              border: 'none',
              borderTop: '1px solid #d1d1d6',
            }}
          />
        </div>
      </div>

      {/* Partie droite */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f3f5',
          padding: `${MIN_MARGIN}px`,
          boxSizing: 'border-box',
        }}
      >
        {currentSession ? (
          <div
            style={{
              width: `${containerWidth}px`,
              height: `${containerHeight}px`,
              backgroundColor: '#ffffff',
              border: '1px solid #d1d1d6',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
              {/* Barre du haut - hauteur fixe */}
              <div
                style={{
                  width: '100%',
                  height: `${TOP_BAR_HEIGHT}px`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 8px',
                  gap: '8px',
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid #d1d1d6',
                  boxSizing: 'border-box',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {/* Bouton Télécharger */}
                  <div
                    onClick={handleDownloadPdf}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ddd'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                    style={{
                      padding: '6px 8px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      borderRight: '1px solid #ccc',
                      left: '50%', // Centre par rapport au parent
                      top: '50%', // Centre par rapport au parent
                      userSelect: 'none',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '9px',
                      border: '1px solid #ccc',
                      transition: 'box-shadow 0.2s, background-color 0.6s',
                    }}
                  >
                    <DownloadIcon style={{ width: '16px', height: '16px', fill: '#7A7A7A' }} />
                  </div>



                  {/* Bouton Vue Trajectoire */}
                  <button
                   // on click toggle view mode + set Pause
                   
                    onClick={toggleViewMode}

                    style={{
                      padding: '8px 12px',
                      borderRadius: '9px',
                      border: 'none',
                      backgroundColor: viewMode === 'scene' ? '#007bff' : '#0063cd',
                      color: '#fff',
                      cursor: 'pointer',
                      height: '32px',
                      boxShadow:
                        viewMode === 'scene'
                          ? 'none'
                          : 'inset 2px 2px 5px rgba(0, 0, 0, 0.3)',
                      transition: 'box-shadow 0.2s, background-color 0.2s',


                    }}
                  >
                    Vue Trajectoire
                  </button>
                </div>


              {/* --- NEW : Sélecteur de curseur, centré */}
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <div
                  style={{
                    position: 'absolute', // Permet de le placer indépendamment du flux
                    left: '50%', // Centre par rapport au parent
                    top: '50%', // Centre par rapport au parent
                    transform: 'translate(-50%, -50%)', // Ajustement parfait en X et Y
                    display: 'flex',
                    border: '1px solid #ccc',
                    borderRadius: '9px',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                  }}
                >
                  {/* Curseur "normal" */}
                  <div
                    onClick={() => setCursorMode('default')}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: cursorMode === 'default' ? '#ddd' : '#fff',
                      cursor: 'pointer',
                      borderRight: '1px solid #ccc',
                      userSelect: 'none',
                      display: 'flex', // Active Flexbox
                      justifyContent: 'center', // Centre horizontalement
                      alignItems: 'center', // Centre verticalement
                      width: '15px', // Largeur fixe
                      flexShrink: 0, // Ne rétrécit pas
                    }}
                  >
                    <img 
                      src={defaultCursor} 
                      alt="Default Cursor" 
                      style={{ width: '20px', height: '20px' }} 
                    />
                  </div>


                  {/* Curseur rond gris */}
                  <div
                    onClick={() => setCursorMode('circle')}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: cursorMode === 'circle' ? '#ddd' : '#fff',
                      cursor: 'pointer',
                      borderRight: '1px solid #ccc',
                      userSelect: 'none',
                      width: '15px', // Largeur fixe
                      flexShrink: 0, // Ne rétrécit pas
                    }}
                  >
                    <span style={{ fontSize: '17px', color: 'gray' }}>●</span>
                  </div>

                  {/* Curseur étoile */}
                  <div
                    onClick={() => setCursorMode('star')}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: cursorMode === 'star' ? '#ddd' : '#fff',
                      cursor: 'pointer',
                      borderRight: '1px solid #ccc',
                      userSelect: 'none',
                      display: 'flex', // Active Flexbox
                      justifyContent: 'center', // Centre horizontalement
                      alignItems: 'center', // Centre verticalement
                      width: '15px', // Largeur fixe
                      flexShrink: 0, // Ne rétrécit pas
                    }}
                  >
                    {selectedSkillId
                      ? ( // Si un skill est choisi, étoile pleine de la couleur du skill
                        <span
                          style={{
                            color: (skills.find((sk) => sk.id === selectedSkillId)?.color) || '#000',
                          }}
                        >
                          ★
                        </span>
                      ) : ( // sinon étoile juste "noire" ou grisée
                        <span style={{ color: '#666' }}>★</span>
                      )
                    }
                  </div>

                  {/* Curseur poubelle */}
                  <div
                    onClick={() => setCursorMode('trash')}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: cursorMode === 'trash' ? '#FFCCD5' : '#fff',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '15px',
                      flexShrink: 0,
                    }}
                  >
                    <img 
                      src={trashIcon} 
                      alt="Trash Cursor" 
                      style={{ width: '20px', height: '20px' }} 
                    />
                  </div>
                  
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                    style={{
                      
                      display: 'flex',
                      borderRadius: '9px',
                      overflow: 'hidden',
                      backgroundColor: '#fff',
                      height: '32px',
                    }}
                  >
                    
                    <button
                      onClick={runOptimization}
                      style={{
                        padding: '6px 10px',
                        borderLeft: '9px',
                        border: 'none',
                        backgroundColor: '#34c759',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      Lancer l'optimisation
                    </button>
                    <button
                      onClick={() => setForcedPosition(!forcedPosition)}
                      style={{
                        marginLeft: '0px',
                        padding: '4px',
                        paddingRight: '5px',
                        border: '1px solid #ccc',
                        borderTopRightRadius: '9px',
                        borderBottomRightRadius: '9px',
                        borderLeft: 'none',
                        cursor: 'pointer',
                        borderRight: '9px',
                        display: 'flex',
                        alignItems: 'center',
                        width: '30px',
                        justifyContent: 'center',
                      }}
                      title="Le cadenas permet de conserver les positions déjà assignées lors de l'optimisation "
                    >
                      {forcedPosition ? <ClosedLock style={{ width: '16px', height: '16px' }} /> : <OpenedLock style={{ width: '16px', height: '16px' }} />}

                    </button>
                  </div>
                </div>    


            </div>

            {/* Canvas */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: `${canvasHeight}px`,
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <canvas
                ref={canvasRef}
                width={canvasPixelWidth}
                height={canvasPixelHeight}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  cursor:
                    cursorMode === 'default'
                      ? 'crosshair'
                      : cursorMode === 'circle'
                      ? 'crosshair' // ou un 'url(...)' si tu as un PNG
                      : 'crosshair', // idem pour star
                }}
                onMouseDown={onCanvasMouseDown}
                onMouseMove={onCanvasMouseMove}
                onMouseUp={onCanvasMouseUp}
                onContextMenu={onCanvasContextMenu}
              />

              {/* Si vue aucun danseur et vue trajectoire activée  */}
              {(!periods || periods.length > 0) && viewMode === 'trajectory' && (!selectedDancerId || selectedDancerId === null) && (!dancers || dancers.length === 0) && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    padding: '8px 16px',
                    backgroundColor: '#fff',
                    border: '1px solid #c1121f',
                    borderRadius: '8px',
                    color: '#c1121f',
                    fontSize: '13px',
                    fontFamily: 'sans-serif',
                    zIndex: 10,
                  }}
                >
                  Veuillez ajouter un danseur pour voir sa trajectoire
                </div>
              )}

              
              {/* Si aucune période n'est crée  */}
              { (!periods || periods.length === 0) && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    padding: '8px 16px',
                    backgroundColor: '#fff',
                    border: '1px solid #c1121f',
                    borderRadius: '8px',
                    color: '#c1121f',
                    fontSize: '13px',
                    fontFamily: 'sans-serif',
                    zIndex: 10,
                  }}
                >
                  Aucune période existante
                </div>
              )}


              {contextMenuPosition?.visible && (
                <ContextMenu
                  x={contextMenuPosition.x}
                  y={contextMenuPosition.y}
                  options={contextMenuPosition.options}
                  onClose={() =>
                    setContextMenuPosition({
                      ...contextMenuPosition,
                      visible: false,
                      useFixed: false,
                    })
                  }
                  useFixed={contextMenuPosition.useFixed ?? false}
                />
              )}
              
              {dancerContextMenu.visible && (
                <ContextMenu
                  x={dancerContextMenu.x}
                  y={dancerContextMenu.y}
                  options={dancerContextMenu.options}
                  onClose={() =>
                    setDancerContextMenu({
                      ...dancerContextMenu,
                      visible: false,
                    })
                  }
                  useFixed={dancerContextMenu.useFixed ?? true}
                />
              )}
            </div>

            {/* Barre de controle */}
            <div className="control-bar" style={{ height: `${CONTROL_BAR_HEIGHT}px` }}>
            {/* Partie gauche : Timestamp */}
            <div className="timestamp">
              <span>
                {formatTimestamp(currentTime)} / {audioRef.current && audioRef.current.duration ? formatTimestamp(audioRef.current.duration) : '--:--'}
              </span>
            </div>


              {/* Partie centrale : Contrôles de lecture */}
              <div className="controls">
                {/* Bouton pour revenir à la première période */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (periods.length > 0) {
                      setCurrentPeriodIndex(0);
                      const firstPeriod = periods[0];
                      if (firstPeriod && audioRef.current) {
                        audioRef.current.currentTime = firstPeriod.start_time;
                        setCurrentTime(firstPeriod.start_time);
                        setIsPlaying(false)
                      }
                    }
                  }}
                >
                  {'⏮'}
                </button>

                {/* Bouton pour reculer de 1 seconde */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (audioRef.current) {
                      const newTime = Math.max(audioRef.current.currentTime - 1, 0);
                      audioRef.current.currentTime = newTime;
                      setCurrentTime(newTime);
                      setIsPlaying(false)
                    }
                  }}
                >
                  {'⏴'}
                </button>

                {/* Bouton Play/Pause (reste inchangé) */}
                <button
                  className={`control-btn play-btn ${isPlaying ? 'active' : ''}`}
                  onClick={() => setIsPlaying(!isPlaying)}
                >
                  {isPlaying ? '⏸' : '⏵'}
                </button>

                {/* Bouton pour avancer de 1 seconde */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (audioRef.current) {
                      const newTime = Math.min(
                        audioRef.current.currentTime + 1,
                        audioRef.current.duration || Infinity
                      );
                      audioRef.current.currentTime = newTime;
                      setCurrentTime(newTime);
                      setIsPlaying(false)
                    }
                  }}
                >
                  {'⏵'}
                </button>

                {/* Bouton pour aller à la dernière période */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (periods.length > 0) {
                      const lastIndex = periods.length - 1;
                      setCurrentPeriodIndex(lastIndex);
                      const lastPeriod = [...periods].reverse().find(period => period.end_time !== null);
                      if (lastPeriod && audioRef.current) {
                        audioRef.current.currentTime = lastPeriod.start_time;
                        setCurrentTime(lastPeriod.start_time);
                        setIsPlaying(false)
                      }
                    }
                  }}
                >
                  {'⏭'}
                </button>
              </div>



              {/* Partie droite : Volume, Fullscreen et Toggle 3D/2D */}
              <div className="extra-controls">
                <div className="divider"></div>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  className="volume-slider" 
                  value={volume * 100}
                  onChange={(e) => {
                    const vol = e.target.value / 100;
                    setVolume(vol);
                    if (audioRef.current) {
                      audioRef.current.volume = vol;
                    }
                  }}
                  onMouseUp={(e) => e.target.blur()}
                  onTouchEnd={(e) => e.target.blur()}
                />

                <div className="divider"></div>

                {/* Bouton 3D */}
                <button 
                  className={`toggle-btn threeD-btn ${isThreeDMode ? 'active' : ''}`} 
                  onClick={() => setIsThreeDMode(!isThreeDMode)}
                >
                  {isThreeDMode ? '3D' : '2D'}
                </button>

                <div className="divider"></div>

                {/* Bouton Fullscreen */}
                <button 
                  className={`toggle-btn ${isFullscreen ? 'active' : ''}`} 
                  onClick={() => {
                    if (!document.fullscreenElement) {
                      // Demande le mode plein écran sur l'élément désiré, ici on prend document.documentElement
                      document.documentElement.requestFullscreen().catch(err => {
                        console.error("Erreur lors de la demande du mode plein écran :", err);
                      });
                      setIsFullscreen(true);
                    } else {
                      document.exitFullscreen().catch(err => {
                        console.error("Erreur lors de la sortie du mode plein écran :", err);
                      });
                      setIsFullscreen(false);
                    }
                  }}
                >
                  {'⛶'}
                </button>

              </div>
            </div>

            <div
              style={{
                width: '100%',
                height: `${BOTTOM_BAR_HEIGHT - CONTROL_BAR_HEIGHT}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#efefef',
                borderTop: '1px solid #d1d1d6',
                overflow: 'hidden',
                position: 'relative', // Permet de positionner la croix
              }}
            >
              {/* Bouton de fermeture rectangulaire dans l'angle supérieur droit */}
                {currentSession?.musics.length > 0 && (
                <div
                  onClick={async () => {
                    if (!currentSession || currentSession.musics.length === 0) return;
                  
                    const musicId = currentSession.musics[0].id; // Supposons qu'il n'y ait qu'une seule musique
                  
                    try {
                      await axios.delete(`${API_BASE_URL}/sessions/${currentSession.id}/musics/${musicId}`, {
                        headers: { Authorization: `Bearer ${user.access_token}` },
                      });
                    
                      // Mise à jour du state pour retirer la musique et supprimer les timestamps des périodes
                      const updatedSession = {
                        ...currentSession,
                        musics: [],
                        periods: currentSession.periods.map(period => ({
                          ...period,
                          start_time: null,
                          end_time: null,
                        })),
                      };
                      setCurrentSession(updatedSession);
                      alert("Musique dissociée avec succès !");
                    } catch (error) {
                      console.error("Erreur lors de la dissociation de la musique :", error);
                      alert("Impossible de dissocier la musique.");
                    }
                  }}
                  
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: `${amplitudeSliderWidth}px`,
                    height: `${timelineHeight}px`,
                    backgroundColor: '#efefef',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                    zIndex: 5, // Pour passer au-dessus de la timeline
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = '#ddd';
                  }}
                  
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.6';
                    e.currentTarget.style.backgroundColor = '#efefef';
                  }}
                >
                  <span style={{ fontSize: '16px', color: '#888' }}>✕</span>
                </div>
              )}

              {currentSession?.musics?.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {currentSession.musics.map((music) => (
                    <div key={music.id} style={{ flex: 1, height: '100%' }}>
                      
                      
                      <MusicSpectrogram
                        music={music}
                        sessionId={currentSession.id}
                        user={user}
                        periods={periods}
                        currentPeriodIndex={currentPeriodIndex}
                        bottomBarHeight={BOTTOM_BAR_HEIGHT}
                        controlBarHeight={CONTROL_BAR_HEIGHT}
                        currentTime={currentTime}
                        onTimeUpdate={setCurrentTime}
                        audioRef={audioRef}
                        onStopAnimation={stopAnimation}
                        onPeriodUpdate={(update) => {
                          // Si c'est juste une sélection, mettre à jour l'index et sortir sans rafraîchir
                          if (update && update.select) {
                            setCurrentPeriodIndex(update.periodIndex);
                            return;
                          }
                          // Sinon, pour une mise à jour de la période, rafraîchir les données depuis le serveur
                          const selectedPeriodId = periods[currentPeriodIndex]?.id;
                          axios
                            .get(`${API_BASE_URL}/sessions/${sessionId}`, {
                              headers: { Authorization: `Bearer ${user.access_token}` },
                            })
                            .then((response) => {
                              // Tri par order_index désormais
                              const sortedPeriods = (response.data.periods || []).slice().sort((a, b) => a.order_index - b.order_index);
                              setCurrentSession({
                                ...response.data,
                                periods: sortedPeriods,
                              });
                              setPeriods(sortedPeriods);
                              if (selectedPeriodId) {
                                const newIndex = sortedPeriods.findIndex((p) => p.id === selectedPeriodId);
                                setCurrentPeriodIndex(newIndex !== -1 ? newIndex : 0);
                              } else {
                                setCurrentPeriodIndex(0);
                              }
                            })
                            .catch((error) => {
                              console.error('Erreur lors de la mise à jour des périodes :', error);
                            });
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  onDragOver={handleMusicDragOver}
                  onDragLeave={handleMusicDragLeave}
                  onDrop={handleMusicDrop}
                  onClick={handleMusicClick}
                  style={{
                    flex: 1,
                    maxWidth: '90%',
                    height: '60px',
                    backgroundColor: musicIsDragging ? '#fff0f3' : '#f0f0f5',
                    border: '2px dashed #ccc',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.2s ease-in-out',
                  }}
                >
                  {musicIsDragging
                    ? 'Déposez votre fichier audio...'
                    : 'Glissez une musique ou cliquez ici pour l\'ajouter. (10 Mo max)'
                  }
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    accept="audio/*"
                    onChange={handleMusicFileInputChange}
                  />
                </div>
              )}
            </div>

          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              color: '#6c6c6c',
              fontSize: '18px',
              padding: '20px',
            }}
          >
            Chargement de la session...
          </div>
        )}
      </div>
      
      {/* 🔥 Overlay d'optimisation avec effet de fondu */}
      {isOptimizing && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // Fond semi-transparent
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999, // Passe au-dessus de tout
            opacity: 1, // Pour la transition
            animation: 'fadeInBackground 0.3s ease-in-out', // Ajoute une animation SANS zoom
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              width: '300px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
              transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out', // Ajout d'une transition
              transform: 'scale(1)', // Effet de zoom uniquement pour cette boîte
              animation: 'fadeInZoom 0.3s ease-in-out',
            }}
          >
            <h3>Optimisation en cours</h3>
            <p>Veuillez patienter...</p>

            {/* Barre de progression */}
            <div
              style={{
                width: '100%',
                height: '10px',
                backgroundColor: '#e0e0e0',
                borderRadius: '5px',
                overflow: 'hidden',
                marginTop: '10px',
              }}
            >
              <div
                style={{
                  width: `${(Number(elapsedTime || 0) / 30) * 100}%`,
                  height: '100%',
                  backgroundColor: '#ff477e',
                  transition: 'width 0.1s linear',
                }}
              />
            </div>

            {/* Temps écoulé sécurisé */}
            <p style={{ marginTop: '10px' }}>
              {Number(elapsedTime || 0).toFixed(2)} / 30 sec
            </p>
          </div>
        </div>
      )}

      {/* Ajout des styles globaux pour les animations */}
      <style>
        {`
          @keyframes fadeInBackground {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes fadeInZoom {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes fadeOut {
            from {
              opacity: 1;
              transform: scale(1);
            }
            to {
              opacity: 0;
              transform: scale(0.9);
            }
          }
        `}
      </style>


    </div>
    
  );
}
